import React, { useState, useRef, useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack'; // Added Stack
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar'; // Added Snackbar
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import QrCodeIcon from '@mui/icons-material/QrCode';
import Webcam from 'react-webcam';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { useAuth } from '@clerk/clerk-react'; // Import useAuth

// Import components
import ResultsSidebar from '../components/ResultsSidebar';

/**
 * Toggle this flag to switch between production and development API endpoints.
 * Set to true for production, false for local development.
 */
const IS_PRODUCTION = true;

const API_BASE_URL = IS_PRODUCTION
  ? 'https://api.covervisionai.com/api/v1'
  : 'http://localhost:8000/api/v1';

// Define the BookResult interface
interface BookResult {
  id: number;
  isbn: string;
  title: string;
  author: string;
  binding_type: string;
  sales_rank: number;
  cover_url: string;
  distance: number;
  confidence: number;
  no_reliable_match?: boolean;
}

// Define interfaces for queue management
interface ProcessingQueueItem {
  id: number;
  imageBlob: Blob;
  timestamp: number;
  bindingType: string;
  status: 'queued' | 'processing' | 'completed' | 'error';
  retryCount: number;
}

// Define the ScanResult interface (adding id and fields for unmatched books)
interface ScanResult {
  id: number; // Unique ID for this scan request
  match_status?: 'found' | 'not_found' | 'error'; // Added for unmatched books
  identifier?: string; // ASIN or ZB_ID
  description?: string; // Book title or LLM-generated description
  results: BookResult[];
  bindingType: string;
  timestamp: number; // Original request time
}

// Session storage key
const SCAN_HISTORY_KEY = 'covervision_scan_history';
const MAX_HISTORY_LENGTH = 20;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      {...other}
    >
      {/* Responsive padding for TabPanel content */}
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

const Search: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [bindingType, setBindingType] = useState('paperback');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchResults, setSearchResults] = useState<BookResult[] | null>(null); // Results of the *latest displayed* scan
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]); // History of *displayed* scans
  const [processingQueue, setProcessingQueue] = useState<ProcessingQueueItem[]>([]); // Items actively being processed or waiting
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submissionOrder, setSubmissionOrder] = useState<number[]>([]); // IDs in the order they were submitted (Used in setNextExpectedId logic)
  const [completedScans, setCompletedScans] = useState<Record<number, ScanResult | { error: string; id: number; bindingType: string; timestamp: number }>>({}); // Holds completed scans before display
  const [nextExpectedId, setNextExpectedId] = useState<number | null>(null); // ID of the next scan to display
  const [queueSnackbarOpen, setQueueSnackbarOpen] = useState(false); // Snackbar state
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getToken } = useAuth(); // Get Clerk's getToken function

  // Load scan history from session storage on component mount
  useEffect(() => {
    const storedHistory = sessionStorage.getItem(SCAN_HISTORY_KEY);
    if (storedHistory) {
      try {
        const parsedHistory = JSON.parse(storedHistory);
        setScanHistory(parsedHistory);
      } catch (err) {
        console.error('Error parsing scan history from session storage:', err);
      }
    }
  }, []);

  // Update session storage when scan history changes
  useEffect(() => {
    if (scanHistory.length > 0) {
      sessionStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(scanHistory));
    }
  }, [scanHistory]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Reset state when changing tabs
    setCapturedImage(null);
    setSelectedFile(null);
    setError(null);
  };

  const handleBindingTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBindingType(event.target.value);
  };

  const captureAndSearch = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return;

      // Convert base64 to blob
      fetch(imageSrc)
        .then(res => res.blob())
        .then(blob => {
          const newItemId = Date.now();
          const queueItem: ProcessingQueueItem = {
            id: newItemId,
            imageBlob: blob,
            timestamp: newItemId, // Use ID as timestamp for simplicity here
            bindingType,
            status: 'queued',
            retryCount: 0
          };

          // Add to processing queue and submission order
          setProcessingQueue(prev => {
            const newQueue = [...prev, queueItem];
            if (newQueue.length > 20) {
              console.warn('Maximum queue size reached (20). Oldest item will be removed.');
              setQueueSnackbarOpen(true); // Show snackbar
              return newQueue.slice(newQueue.length - 20); // Keep the newest 20
            }
            return newQueue;
          });
          setSubmissionOrder(prev => [...prev, newItemId]);
          if (nextExpectedId === null) {
            setNextExpectedId(newItemId);
          }

          // Reset webcam view for next scan
          setCapturedImage(null);
        });
    }
  }, [webcamRef, bindingType, nextExpectedId]); // Added nextExpectedId dependency

  // Handle keyboard events for spacebar capture
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only respond if webcam tab is active and webcam is ready
      if (tabValue === 0 && !capturedImage && !isLoading && e.code === 'Space') {
        e.preventDefault(); // Prevent page scrolling
        captureAndSearch();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [tabValue, capturedImage, isLoading, captureAndSearch]); // Keep dependencies as they were

  // Handle keyboard events for 'P', 'H', and 'B' to change binding type
  useEffect(() => {
    const handleBindingKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'p' || e.key === 'P') {
        setBindingType('paperback');
        console.log('Binding type set to Paperback via shortcut');
      } else if (e.key === 'h' || e.key === 'H') {
        setBindingType('hardcover');
        console.log('Binding type set to Hardcover via shortcut');
      } else if (e.key === 'b' || e.key === 'B') {
        setBindingType('barcode');
        console.log('Binding type set to Barcode via shortcut');
      }
    };

    document.addEventListener('keydown', handleBindingKeyPress);
    return () => document.removeEventListener('keydown', handleBindingKeyPress);
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount

  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
    }
  }, [webcamRef]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetCapture = () => {
    setCapturedImage(null);
  };

  const resetFileUpload = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Process the queue whenever it changes
  useEffect(() => {
    const processQueueItem = async (queueItem: ProcessingQueueItem) => {
      try {
        // Process the image
        const formData = new FormData();
        formData.append('binding_type', queueItem.bindingType);
        formData.append('top_k', '5');  // Changed from '7' to '5'
        formData.append('timestamp', queueItem.timestamp.toString());
        formData.append('file', queueItem.imageBlob, `webcam-capture-${queueItem.timestamp}.jpg`);

        // Get auth token
        const token = await getToken({ template: "covervision-backend" });
        if (!token) {
          throw new Error("Authentication token not available.");
        }

        // Send to API with timeout and Auth header
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        const response = await axios.post(`${API_BASE_URL}/search/by-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`, // Add Authorization header
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle the new response format
        const responseData = response.data;
        
        // Create scan result with ID and unmatched book support
        const scanResult: ScanResult = {
          id: queueItem.id, // Use the queue item's ID
          bindingType: queueItem.bindingType,
          timestamp: queueItem.timestamp, // Keep original timestamp
          
          // Handle both new structured format and old format
          match_status: responseData.match_status,
          identifier: responseData.identifier,
          description: responseData.description,
          // If we have a new format with results array, use it, otherwise the data itself is the results array
          results: responseData.results || responseData
        };

        return { success: true, data: scanResult };
      } catch (error: unknown) {
        console.error(`Error processing item ${queueItem.id}:`, error);
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            throw new Error('Request timed out');
          }
          throw error;
        }
        throw new Error('An unknown error occurred');
      }
    };

    const processQueue = async () => {
      // Get all queued items
      const queuedItems = processingQueue.filter(item => item.status === 'queued');
      if (queuedItems.length === 0) return;

      // Process up to 5 items concurrently
      const itemsToProcess = queuedItems.slice(0, 5);

      // Mark items as 'processing' *before* starting the async work
      const processingIds = itemsToProcess.map(item => item.id);
      setProcessingQueue(prev =>
        prev.map(qi => processingIds.includes(qi.id) ? { ...qi, status: 'processing' } : qi)
      );

      const processingPromises = itemsToProcess.map(async (item) => {
        try {
          const result = await processQueueItem(item);
          // Store successful result using functional update
          setCompletedScans(prev => ({ ...prev, [item.id]: result.data }));
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
          // Retry logic or store error
          if (item.retryCount < 1 && errorMessage !== 'Request timed out') {
            // Retry once: Reset status to queued and increment retry count
            console.log(`Retrying item ${item.id}`);
            setProcessingQueue(prev =>
              prev.map(qi => qi.id === item.id ? { ...qi, status: 'queued', retryCount: qi.retryCount + 1 } : qi)
            );
            // Don't store error yet, let it retry
          } else {
            // Store error result after exhausting retries or on timeout
            console.log(`Storing error for item ${item.id}`);
            const errorData = {
              error: errorMessage === 'Request timed out' ? 'Processing timed out' : 'Processing failed',
              id: item.id,
              bindingType: item.bindingType,
              timestamp: item.timestamp
            };
            // Store error result using functional update
            setCompletedScans(prev => ({ ...prev, [item.id]: errorData }));
             // Since it failed definitively, mark its status as 'error' in the main queue
             // It will be removed later by processCompletedScans
             setProcessingQueue(prev =>
               prev.map(qi => qi.id === item.id ? { ...qi, status: 'error' } : qi)
             );
          }
        }
        // No finally block needed here to update queue status, handled by completion/error storage
        // and the subsequent processCompletedScans effect
      });

      // Wait for all concurrent processing attempts to finish (or fail)
      // We don't need the results here, just wait for them to complete
      await Promise.allSettled(processingPromises); // Use allSettled to wait for all, even if some reject
    };

    // Trigger processing if there are queued items
    if (processingQueue.some(item => item.status === 'queued')) {
      processQueue();
    }
  }, [processingQueue, getToken]); // Keep dependencies

  // New useEffect to process completed scans in order
  useEffect(() => {
    // This effect runs when completedScans changes.
    // It checks if the scan corresponding to nextExpectedId is now complete.
    if (nextExpectedId !== null && completedScans[nextExpectedId]) {
      const completedScanData = completedScans[nextExpectedId];
      const processedId = completedScanData.id;
      let scanResultForHistory: ScanResult;

      // Handle success or error
      if ('results' in completedScanData) { // Successful scan
        setSearchResults(completedScanData.results);
        scanResultForHistory = completedScanData;
        console.log(`Displayed result for ID: ${processedId}`);
      } else { // Error scan
        scanResultForHistory = {
          id: completedScanData.id,
          results: [{
            id: -1, isbn: 'error', title: completedScanData.error,
            author: completedScanData.error === 'Processing timed out' ? 'Request took too long' : 'Could not process image',
            binding_type: completedScanData.bindingType, sales_rank: 0, cover_url: '',
            distance: 0, confidence: 0, no_reliable_match: true
          }],
          bindingType: completedScanData.bindingType,
          timestamp: completedScanData.timestamp
        };
        setSearchResults(scanResultForHistory.results);
        console.log(`Displayed error for ID: ${processedId}`);
      }

      // Update history and session storage
      const newHistory = [scanResultForHistory, ...scanHistory].slice(0, MAX_HISTORY_LENGTH);
      setScanHistory(newHistory);
      sessionStorage.setItem(SCAN_HISTORY_KEY, JSON.stringify(newHistory));

      // Clean up states using functional updates
      setCompletedScans(prevScans => {
        const newState = { ...prevScans };
        delete newState[processedId];
        return newState;
      });
      setProcessingQueue(prevQueue => prevQueue.filter(item => item.id !== processedId));

      // Update submissionOrder and determine the next expected ID
      setSubmissionOrder(prevOrder => {
        const newOrder = prevOrder.filter(id => id !== processedId);
        // Set the nextExpectedId based on the *updated* order
        setNextExpectedId(newOrder.length > 0 ? newOrder[0] : null);
        return newOrder;
      });
    }
    // Dependencies: This effect should re-run if the map of completed scans changes,
    // or if the ID we are currently expecting changes.
  }, [completedScans, nextExpectedId, scanHistory]); // Added scanHistory back as it's used to build newHistory


  // Refactored searchByImage to use the queue for both webcam and file uploads
  const searchByImage = async () => {
    // No longer directly calls API, just adds to queue
    setIsLoading(true); // Keep loading indicator for immediate feedback
    setError(null);
    // Don't reset searchResults here, let the queue handle updates
    let imageBlob: Blob | null = null;
    // let sourceName = ''; // Removed unused variable
    const newItemId = Date.now();

    try {
      if (tabValue === 0 && capturedImage) {
        const response = await fetch(capturedImage);
        imageBlob = await response.blob();
        // sourceName = `webcam-capture-${newItemId}.jpg`; // Removed unused assignment
        setCapturedImage(null); // Reset webcam view immediately
      } else if (tabValue === 1 && selectedFile) {
        imageBlob = selectedFile;
        // sourceName = `${selectedFile.name.split('.')[0]}-${newItemId}.${selectedFile.name.split('.').pop()}`; // Removed unused assignment
        resetFileUpload(); // Reset file upload view immediately
      } else {
        throw new Error('No image source selected');
      }

      if (imageBlob) {
        const queueItem: ProcessingQueueItem = {
          id: newItemId,
          imageBlob: imageBlob,
          timestamp: newItemId,
          bindingType,
          status: 'queued',
          retryCount: 0
        };

        // Add to processing queue and submission order
        setProcessingQueue(prev => {
          const newQueue = [...prev, queueItem];
          if (newQueue.length > 20) {
             console.warn('Maximum queue size reached (20). Oldest item will be removed.');
             setQueueSnackbarOpen(true); // Show snackbar
             return newQueue.slice(newQueue.length - 20); // Keep the newest 20
          }
          return newQueue;
        });
        setSubmissionOrder(prev => [...prev, newItemId]);
        if (nextExpectedId === null) {
          setNextExpectedId(newItemId);
        }
      }

    } catch (err) {
      console.error('Error preparing image for queue:', err);
      setError('Error preparing image. Please try again.');
    } finally {
      // Set loading false quicker, as the actual processing is async
      // The sidebar will show the queue status
      setIsLoading(false);
    }
  };
  // Generate a QR code URL that points to the search page
  const qrCodeUrl = `${window.location.origin}/search`;

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setQueueSnackbarOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, py: 2 }}> {/* Added vertical padding */}
      {/* Adjusted grid layout and spacing */}
      <Grid container spacing={4} sx={{ justifyContent: 'space-between' }}>
        {/* Left side - Search controls */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 3, // Consistent rounding
              height: '100%', // Ensure paper takes full height of grid item
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
              Search by Book Cover
            </Typography>

            <Box sx={{ width: '100%', mb: 3 }}> {/* Reduced margin */}
              <FormControl component="fieldset">
                <Typography variant="subtitle1" gutterBottom>
                  Binding Type:
                </Typography>
                <RadioGroup
                  row
                  name="binding-type"
                  value={bindingType}
                  onChange={handleBindingTypeChange}
                >
                  <FormControlLabel
                    value="hardcover"
                    control={<Radio />}
                    label="Hardcover"
                  />
                  <FormControlLabel
                    value="paperback"
                    control={<Radio />}
                    label="Paperback"
                  />
                  <FormControlLabel
                    value="barcode"
                    control={<Radio />}
                    label="Barcode"
                  />
                </RadioGroup>
                {bindingType === 'barcode' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Use this option to scan an ISBN barcode directly. Point your camera at the barcode on the back of the book.
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="search tabs"
                  centered
                >
                  <Tab
                    icon={<PhotoCameraIcon />}
                    label="Webcam"
                    sx={{ textTransform: 'none' }} // Prevent uppercase
                    id="search-tab-0"
                    aria-controls="search-tabpanel-0"
                  />
                  <Tab
                    icon={<FileUploadIcon />}
                    label="Upload"
                    sx={{ textTransform: 'none' }} // Prevent uppercase
                    id="search-tab-1"
                    aria-controls="search-tabpanel-1"
                  />
                  <Tab
                    icon={<QrCodeIcon />}
                    label="QR Code"
                    sx={{ textTransform: 'none' }} // Prevent uppercase
                    id="search-tab-2"
                    aria-controls="search-tabpanel-2"
                  />
                </Tabs>
              </Box>

              {/* Webcam Tab */}
              <TabPanel value={tabValue} index={0}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {!capturedImage ? (
                    <>
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{
                          width: 480, // Swapped width and height
                          height: 640, // Swapped width and height
                          facingMode: 'environment',
                        }}
                        style={{
                          width: '100%',
                          maxWidth: '640px',
                          borderRadius: '8px',
                          objectFit: 'contain', // 👈 show the whole frame, prevents cropping
                        }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={captureImage}
                        startIcon={<PhotoCameraIcon />}
                      >
                        Capture Image
                      </Button>
                    </>
                  ) : (
                    <>
                      <img
                        src={capturedImage}
                        alt="Captured"
                        style={{
                          width: '100%',
                          maxWidth: '640px',
                          borderRadius: '8px',
                          border: '1px solid', // Add subtle border
                          borderColor: 'divider',
                        }}
                      />
                      {/* Use Stack for buttons */}
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={resetCapture}
                        >
                          Retake
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={searchByImage}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Search'
                          )}
                        </Button>
                      </Stack>
                    </>
                  )}
                </Box>
              </TabPanel>

              {/* Upload Tab */}
              <TabPanel value={tabValue} index={1}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                  />
                  {!selectedFile ? (
                    // Improved Dropzone Style
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 3, md: 4 },
                        textAlign: 'center',
                        width: '100%',
                        maxWidth: '640px',
                        cursor: 'pointer',
                        borderStyle: 'dashed',
                        borderColor: 'grey.400',
                        backgroundColor: 'grey.50',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 150, // Ensure some height
                      }}
                      onClick={handleFileUploadClick}
                    >
                      <FileUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        Click or Drag to Upload
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        (Max file size: 10MB)
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: '640px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid', // Add subtle border
                          borderColor: 'divider',
                        }}
                      >
                        <img
                          src={URL.createObjectURL(selectedFile)}
                          alt="Selected"
                          style={{
                            width: '100%',
                            display: 'block',
                          }}
                        />
                      </Box>
                      <Typography variant="body1" sx={{ mt: 1 }}>
                        {selectedFile.name}
                      </Typography>
                      {/* Use Stack for buttons */}
                      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={resetFileUpload}
                        >
                          Remove
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={searchByImage}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            'Search'
                          )}
                        </Button>
                      </Stack>
                    </>
                  )}
                </Box>
              </TabPanel>

              {/* QR Code Tab */}
              <TabPanel value={tabValue} index={2}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    textAlign: 'center', // Center text
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Scan with your Phone
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                    Open this page on your mobile device to use its camera for capturing book covers.
                  </Typography>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      mt: 1, // Add margin top
                      borderRadius: 2, // Rounded corners
                      backgroundColor: 'white',
                      display: 'inline-block', // Fit content
                    }}
                  >
                    <QRCodeSVG value={qrCodeUrl} size={200} /> {/* Slightly smaller */}
                  </Paper>
                </Box>
              </TabPanel>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 3, width: '100%' }}> {/* Increased margin */}
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Right side - Results sidebar */}
        {/* Adjusted grid item for sidebar */}
        <Grid item xs={12} md={5}>
          <ResultsSidebar
            currentResults={searchResults} // Now shows the latest *displayed* result
            isLoading={isLoading || processingQueue.some(item => item.status === 'processing')} // Show loading if button clicked OR items are processing
            scanHistory={scanHistory} // Shows history of *displayed* results
            // Filter out items that definitively failed ('error') or are waiting to be displayed
            processingQueue={processingQueue.filter(item => item.status === 'queued' || item.status === 'processing')}
            currentScan={scanHistory.length > 0 ? scanHistory[0] : null} // Pass the most recent scan for label printing
          />
        </Grid>
      </Grid>
      {/* Snackbar for queue limit warning */}
      <Snackbar
        open={queueSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Queue limit reached (20). Oldest scans may be removed."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Search;
